/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetBIFilterParam';

const ActionSetBIFilterParamManager: ActionManagerFn<
  'ActionSetBIFilterParam'
> = (platform: Platform): ActionManager<'ActionSetBIFilterParam'> => {
  return {
    ...baseActionManager(platform, 'ActionSetBIFilterParam'),
    defaultValue,
    props: {
      ...baseActionProps,
      schema: {
        label: 'Schema',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      tableName: {
        label: 'Table Name',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      columnName: {
        label: 'Column Name',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      filterType: {
        label: 'Filter Type',
        type: 'select',
        options: toOptions([
          'Advanced',
          'Basic',
          'Unknown',
          'IncludeExclude',
          'RelativeDate',
          'TopN',
          'Tuple',
          'RelativeTime',
        ]),
      },
      operator: {
        label: 'Operator',
        type: 'string',
      },
      value: {
        label: 'Value',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    name: 'Set Filter Param',
    icon: { iconName: 'rocket' },
    description: `**Overview**<br/>
The Set BI Filter Param action is a specialized configuration component designed to define filter constraints for embedded Power BI reports. It functions as a parameter builder, constructing the specific JSON structure required by the Power BI Javascript API to apply filters programmatically when a report is loaded or updated.<br/><br/>
This action allows developers to bridge the context of the application (e.g., the currently selected customer) with the embedded analytics. Instead of showing a generic report, the application can instruct the BI container to slice the data specifically for a target schema, table, and column, ensuring the user sees only relevant metrics.

**Key Features**<br/>
* **Hierarchical Targeting:** Explicitly maps the filter to the data model structure using schema, tableName, and columnName, ensuring precise application to the correct field within the BI dataset.
* **Comprehensive Filter Types:** Supports a wide range of Power BI filtering logic, including Basic (exact match), Advanced (logical conditions), RelativeDate (time windows), and TopN (ranking).
* **Dynamic Injection:** All addressing properties and the value property support expressions, enabling the filter parameters to be calculated at runtime (e.g., passing a URL parameter into the report filter).
* **Operator Flexibility:** Includes an operator property to define the relationship (e.g., "In", "Contains", "LessThan"), allowing for complex query logic beyond simple equality.

**Properties**<br/>
* **Targeting Properties (Required):**
  * *Schema:* The database schema name in the Power BI dataset.
  * *Table Name:* The specific table within that schema.
  * *Column Name:* The field within the table to filter against.
* **Filter Configuration:**
  * *Filter Type:* Selects the logic mode (e.g., Basic, Advanced, RelativeDate, TopN).
  * *Operator:* A string defining the comparison (e.g., In, All).
* **Value (Required):** An expression property returning a string.
  * *Function:* The actual value(s) to filter by (e.g., 'East Region' or '2023-01-01').

**Use Cases**<br/>
* **Contextual Analytics:** Embedding a "Sales Performance" report on a "Region Details" page, and using this action to filter the report automatically to show only data for that specific region.
* **Security & Privacy:** Restricting the view of a sensitive HR report by filtering for the logged-in user's Department ID before the report renders.
* **Date Slicing:** applying a RelativeDate filter to restrict a dashboard to show only "Last 30 Days" of data based on the application's current settings.`,
  };
};

export default ActionSetBIFilterParamManager;
